import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { rdpdService } from '../rdpd.service';
import * as vo from 'src/app/core/model/server/vo/_index';

export interface DistributorTypeData {
  distributorType: 'regional' | 'provincial' | 'retailer';
  parentGuid?: string | null;
}

@Component({
  selector: 'app-add-distributor',
  templateUrl: './add-distributor.component.html',
  styleUrls: ['./add-distributor.component.scss']
})
export class AddDistributorComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  form!: UntypedFormGroup;
  apiError: string | null = null;

  // ─── Convenience getters for template ────────────────────────────────────

  get isRegional(): boolean {
    return this.data.distributorType === 'regional';
  }

  get isProvincial(): boolean {
    return this.data.distributorType === 'provincial';
  }

  get isRetailer(): boolean {
    return this.data.distributorType === 'retailer';
  }

  get distributorTypeLabel(): string {
    const labels: Record<string, string> = {
      regional:   'Regional Distributor',
      provincial: 'Provincial Distributor',
      retailer:   'New Retailer',
    };
    return labels[this.data.distributorType] ?? '';
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<AddDistributorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DistributorTypeData,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private rdpdFacade: rdpdService,
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => { this.apiError = null; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Form Builder ─────────────────────────────────────────────────────────

  private buildForm(): void {
    const mobileValidators = [
      Validators.required,
      Validators.pattern('^(639|632)[0-9]{9}$')
    ];

    const textValidators = [
      Validators.required,
      Validators.minLength(2),
      Validators.pattern('^[a-zA-Z0-9 ]+$'),
    ];

    // Base controls present for all distributor types
    const controls: Record<string, any> = {
      mobileNumber: ['', mobileValidators],
      description:  [''],
    };

    // Regional
    if (this.isRegional) {
      controls['region'] = ['', textValidators];
    }

    // Provincial
    if (this.isProvincial) {
      controls['province'] = ['', textValidators];
    }

    // Retailer: only mobileNumber + description (already in base)

    this.form = this.fb.group(controls);
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  getFieldError(field: string): string | null {
    const ctrl = this.form.get(field);
    if (!ctrl || !ctrl.invalid || !ctrl.touched) { return null; }

    switch (field) {
      case 'mobileNumber':
      if (ctrl.hasError('required')) { return 'Mobile number is required.'; }
      if (ctrl.hasError('pattern')) {
        const val: string = ctrl.value ?? '';
        if (/[^0-9]/.test(val)) { return 'Invalid Mobile Number'; }
        return 'Enter a valid 12-digit mobile number starting with 639.';
      }
      break;

      case 'region':
        if (ctrl.hasError('required'))   { return 'Region is required.'; }
        if (ctrl.hasError('minlength'))  { return 'Region must be at least 2 characters.'; }
        if (ctrl.hasError('pattern'))    { return 'Region must not contain special characters.'; }
        break;

      case 'province':
        if (ctrl.hasError('required'))   { return 'Province is required.'; }
        if (ctrl.hasError('minlength'))  { return 'Province must be at least 2 characters.'; }
        if (ctrl.hasError('pattern'))    { return 'Province must not contain special characters.'; }
        break;
    }

    return null;
  }

  isInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  store(): void {
    if (!this.isFormValid) {
      this.form.markAllAsTouched();
      return;
    }

    this.spinner.show().catch(console.error);
    const v = this.form.value;

    if (this.isRegional) {
      const payload: vo.CreateNewRDRequest = {
        mobileNumber: v.mobileNumber,
        region:       v.region,
        description:  v.description || null,
      };
      this.rdpdFacade.createRegionalDistributor(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => this.handleSuccess(res),
          error: (err) => this.handleError(err),
        });

    } else if (this.isProvincial) {
      const payload: vo.CreateNewPDRequest = {
        mobileNumber:  v.mobileNumber,
        province:      v.province,
        rdAccountGuid: this.data.parentGuid ?? '',
        description:   v.description || null,
      };
      this.rdpdFacade.createNewProvincialDistributor(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => this.handleSuccess(res),
          error: (err) => this.handleError(err),
        });

    } else if (this.isRetailer) {
      const payload: vo.CreateNewRetailerRequest = {
        mobileNumber:  v.mobileNumber,
        pdAccountGuid: this.data.parentGuid ?? '',
        description:   v.description || null,
      };
      this.rdpdFacade.createNewRetailerDistributor(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => { this.handleSuccess(res) },
          error: (err) =>{ this.handleError(err)} ,
        });
    }
  }

  private handleSuccess(res: any): void {
    this.spinner.hide().catch(console.error);
    const distributorType = this.isRetailer ? 'Retailer' : this.isRegional ? 'Regional Distributor' : 'Provincial Distributor'
    if (res?.ok === false || res?.status !== 200) {
      const msg = res?.error?.message ?? res?.message ?? 'Something went wrong. Please try again.';
      this.apiError = msg;
      this.toastr.error(msg, `Failed to add ${distributorType}.`);
      return;
    }
    
    this.toastr.success(`New ${distributorType} has been successfully added.`);
    this.dialogRef.close(true);
  }

  private handleError(err: any): void {
    this.apiError = err?.error?.message ?? 'Something went wrong. Please try again.';
    this.toastr.error(this.apiError ?? 'Failed to add distributor.');
    this.spinner.hide().catch(console.error);
  }


  formatMobileNumber(): void {
    const ctrl = this.form.get('mobileNumber');
    if (!ctrl) { return; }

    let val: string = (ctrl.value ?? '').trim();

    // 09XXXXXXXXX (11 digits) → 639XXXXXXXXX (12 digits)
    if (val.startsWith('09') && val.length === 11) {
      val = '63' + val.slice(1);
      ctrl.setValue(val, { emitEvent: false });
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }
  
}
